/**
 * 菜单/路由 → 所需权限 key（数组内任一满足即可）。
 *
 * 与后端两处保持对齐：
 *  - nestjs-platform/src/auth/platform-permissions.constants.ts 的 PERMISSION_GROUPS
 *  - 各 admin controller 的 @Permissions() 注解
 *
 * 消费方：PlatformLayout（菜单过滤 + 路由闸）、PlatformLoginPage / 首页重定向
 * （firstAllowedPath）、DashboardPage（统计瓦片按权限渲染）。
 * A 端新增菜单页时必须同步本表，否则新页面对所有非超管角色不可见。
 *
 * key 的顺序即菜单顺序 —— firstAllowedPath 按此挑登录后落点。
 */
export const PATH_PERMS: Record<string, string[]> = {
  '/dashboard': ['dashboard.view'],
  '/tenants': ['tenants.view'],
  '/group-relations': ['group_relations.view'],
  '/tenant-relations': ['group_relations.view'],
  '/individual-users': ['individual_users.view'],
  '/finance': ['analytics.financial'],
  '/orders': ['orders.view'],
  '/invoices': ['invoices.review'],
  '/bank-transfer-reviews': ['orders.bank_approve'],
  '/pending-charges': ['pending_charges.manage'],
  '/plans': ['plans.view'],
  '/geo-content/entitlements': ['geo_content.entitlements.view'],
  '/geo-content/templates': ['geo_content.templates.view'],
  '/recharge-promotions': ['recharge_promotions.manage'],
  '/ai-usage': ['analytics.ai_cost'],
  '/rag': ['rag.view'],
  '/aigc': ['aigc.view'],
  '/messages-archive': ['messages_archive.view'],
  '/audit-config': ['audit_config.manage'],
  '/keyword-alerts': ['keyword_alerts.view'],
  '/app-visits': [],
  '/card-channels': [],
  '/cms': ['cms.view'],
  '/legal-docs': ['legal_docs.manage'],
  '/feedbacks': ['feedback.view'],
  '/operations/chat-suggestions': ['chat_suggestions.manage'],
  '/mall-products': ['mall_products.manage'],
  '/themes': ['themes.manage'],
  '/card-templates': ['card_templates.manage'],
  '/system-config': ['system_config.manage'],
  '/staff': ['staff.view'],
  '/roles': ['roles.manage'],
  '/audit-logs': ['audit_logs.view'],
  '/login-logs': ['login_logs.view'],
  '/service-hall': ['service_hall.view'],
};

/** required 为空/未登记的路径不设限；'*' 通配（super_admin）。 */
export function hasPerm(permissions: readonly string[], required: string[] | undefined): boolean {
  if (!required || required.length === 0) return true;
  if (permissions.includes('*')) return true;
  return required.some((p) => permissions.includes(p));
}

/** 按最长前缀匹配当前路径所需权限（/tenants/:id/edit → /tenants）。 */
export function requiredPermsForPath(pathname: string): string[] | undefined {
  let best: string | undefined;
  for (const p of Object.keys(PATH_PERMS)) {
    if ((pathname === p || pathname.startsWith(p + '/')) && (!best || p.length > best.length)) best = p;
  }
  return best ? PATH_PERMS[best] : undefined;
}

/** 菜单顺序里第一个有权限的路径 —— 登录后 / 首页重定向 / 无权限兜底的落点。 */
export function firstAllowedPath(permissions: readonly string[]): string | null {
  for (const p of Object.keys(PATH_PERMS)) {
    if (hasPerm(permissions, PATH_PERMS[p])) return p;
  }
  return null;
}
